import { useEffect } from 'react';
import { roleplayRoomName, type RoleplayEvent } from '@wundo/shared/roleplay.ts';
import { isChatEvent } from './chatEvent.ts';
import { connectRoom } from '@wundo/client/roomSocket.ts';

function isRoleplayEvent(
  data: Record<string, unknown>,
): data is RoleplayEvent & Record<string, unknown> {
  return data.type === 'suggest_end' || isChatEvent(data);
}

/**
 * Listen to one practice session for as long as it is on screen.
 *
 * The room speaks the chat drawer's own events — the character's replies as
 * they are written, and the sign that it is answering — plus its own signal
 * that the conversation has run its course. Writing to the character is a
 * request to the API, not something said on this socket.
 *
 * `onEvent` and `onReconnect` are expected to be stable, so the socket is not
 * torn down and rebuilt on every keystroke.
 */
export function useRoleplayRoom(
  sessionId: string | null,
  onEvent: (event: RoleplayEvent) => void,
  onReconnect: () => void,
): void {
  useEffect(() => {
    if (sessionId === null) {
      return;
    }

    return connectRoom(
      roleplayRoomName(sessionId),
      (data) => {
        /* Anything that is not a shape we know is ignored rather than guessed at. */
        if (isRoleplayEvent(data)) {
          onEvent(data);
        }
      },
      onReconnect,
    );
  }, [sessionId, onEvent, onReconnect]);
}
